import type { PIMItem, Warehouse } from "@/types/pim";

/**
 * Pure display helpers shared by the server-side card projection
 * (lib/item-card-dto.ts) and the item detail page (which still works with
 * the full PIMItem). Moved out of InventoryList.ItemCard so the card component
 * can consume the pre-computed ItemCardDTO instead.
 */

// Add category IDs that should include the year in the name.
// (Empty for Dealer Rack today — the card has never shown a year here; the
// gandpconstruction reference uses [37]. Add IDs to opt categories in.)
export const categoriesToIncludeYearInName: number[] = [];

export const getItemName = (item: PIMItem) => {
  // Add any custom logic for formatting item names here
  if (categoriesToIncludeYearInName.includes(item._CATEGORY) && item._ATTRIBUTES?.["Year"]?.Value) {
    return `${item._ATTRIBUTES["Year"].Value} ${item.Name}`;
  }
  return item.Name;
};

export const getListOfLocations = (item: PIMItem): string[] => {
  const warehouses: Warehouse[] = item._WAREHOUSES
    ?? Object.values(item._WAREHOUSE || {});

  const ret = warehouses.map((wh) => {
    const regex = /^[a-zA-Z\s]+,\s[a-zA-Z\s]+\s-\s[a-zA-Z0-9\s]+$/;
    if (wh.QuantityAvailable && wh.QuantityAvailable > 0) {
      if (wh.WarehouseCity && wh.WarehouseState) {
        return wh.WarehouseCity + ", " + wh.WarehouseState;
      } else if (wh.WarehouseName && wh.WarehouseName.includes(", G&P")) {
        return wh.WarehouseName.split(", G&P")[0].trim();
      } else if (wh.WarehouseName && wh.WarehouseName.includes(" - ")) {
        return wh.WarehouseName.split(" - ")[0].trim();
      } else if (regex.test(wh.WarehouseName)) {
        const parts = wh.WarehouseName.split(' - ');
        return parts.length > 1 ? parts[0].trim() : wh.WarehouseName.trim();
      }
    }
    return null;
  }).filter((s): s is string => s != null && s.trim() != ',') || [];

  return ret;
};

export const getAvailabilityByLocation = (item: PIMItem): { location: string; quantity: number }[] => {
  const warehouses: Warehouse[] = item._WAREHOUSES
    ?? Object.values(item._WAREHOUSE || {});

  const availability: ({ location: string; quantity: number } | null)[] = warehouses.map((wh) => {
    const regex = /^[a-zA-Z\s]+,\s[a-zA-Z\s]+\s-\s[a-zA-Z0-9\s'&.#-]+$/;
    let location = null;
    if (wh.QuantityAvailable && wh.QuantityAvailable > 0) {
      if (wh.WarehouseCity && wh.WarehouseState) {
        location = wh.WarehouseCity + ", " + wh.WarehouseState;
      } else if (wh.WarehouseName && wh.WarehouseName.includes(", G&P")) {
        location = wh.WarehouseName.split(", G&P")[0].trim();
      } else if (regex.test(wh.WarehouseName)) {
        const parts = wh.WarehouseName.split(' - ');
        location = parts.length > 1 ? parts[0].trim() : wh.WarehouseName.trim();
      }
    }
    if (location) {
      return {
        location,
        quantity: wh.QuantityAvailable || 0
      };
    }
    return null;
  }).filter(s => s != null && s.location?.trim() != ',' && s.quantity > 0) || [];
  return availability as { location: string; quantity: number }[];
};
